/**
 * Which ladder steps a given persona owns (B2, reused by B6).
 *
 * The journey spine (P274) already derives, live, where the bank sits
 * on the 19-step ladder. Each step carries an owner persona in the
 * shared catalogue, so "her next step" is a filter over work the
 * product already knows about -- not a second ladder to keep in sync.
 *
 * Pure, so the ordering rule is testable without a browser.
 */
import { STEP_META, type Owner } from "shared/journey/stepMeta";
import type { JourneySpineStep } from "shared/api/types";

/** Steps this persona owns, most-pressing first. */
export function stepsForOwner(
  steps: JourneySpineStep[] | undefined,
  owner: Owner,
): JourneySpineStep[] {
  if (!steps) return [];
  const mine = steps.filter((s) => STEP_META[s.key]?.owner === owner);
  // Blocked leads: it names the thing that has to happen first, and
  // acting on anything else while it stands is wasted effort. Then
  // partial (started, unfinished), then not started. Done drops out --
  // a "next step" list that lists finished work is a progress bar
  // pretending to be an instruction.
  const rank: Record<string, number> = { blocked: 0, partial: 1, todo: 2 };
  return mine
    .filter((s) => s.status !== "done")
    .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || a.stage - b.stage);
}

export function routeForStep(key: string): string | null {
  return STEP_META[key]?.route ?? null;
}
